import { Option, AllUsersType, UserAvatars } from './customTypes';
import get from 'lodash/get';
import { RangeFilter } from './types/filters';
import { USER_STATUS, USER_TYPES, USER_ROLES, LICENSE_TIER } from './constants';
import intl from 'react-intl-universal';
import userAvatar from 'assets/icons/user-avatar.svg';

export const shouldDisplayBudgetLink = (
  flagEnabled: boolean | undefined,
  userType: typeof USER_TYPES[keyof typeof USER_TYPES] | undefined,
  userRole: typeof USER_ROLES[keyof typeof USER_ROLES] | undefined
) => {
  if (!flagEnabled) return false;

  if (userRole === USER_ROLES.ADMIN) return true;

  const restrictedUserTypes = [
    USER_TYPES.L_D,
    USER_TYPES.FACILITATOR,
    USER_TYPES.BUSINESS,
    USER_TYPES.EXTERNAL,
  ];

  if (!restrictedUserTypes.includes(userType as any)) {
    return true;
  }

  return false;
};
export const isUserStatusDisabled = (status: string = '') => {
  return (
    status === USER_STATUS.REGISTERED_DISABLED ||
    status === USER_STATUS.INVITED_DISABLED
  );
};

/**
 * License-consuming user types. Both L&D and Facilitator users occupy a
 * license seat, so both must be subject to the no-licenses-available
 * deactivation rule. Keeping this in one place prevents the two surfaces
 * (the Add User warning banner and the new-user status assignment) from
 * drifting apart (see CSI-744).
 */
const LICENSE_CONSUMING_USER_TYPES = [USER_TYPES.L_D, USER_TYPES.FACILITATOR];

/**
 * Whether a newly invited user should be created as deactivated because the
 * organization has no licenses left. Trial organizations are exempt.
 */
export const shouldDeactivateNewUserForLicenses = (
  userType: string | undefined,
  licenseTier: string | undefined,
  availableLicenses: number
): boolean => {
  return (
    LICENSE_CONSUMING_USER_TYPES.includes(userType as string) &&
    licenseTier !== LICENSE_TIER.TRIAL &&
    availableLicenses < 1
  );
};

export const getInitialValueForDropDown = (
  options: Option[],
  values: string[] | string | undefined | null,
  defaultValueName?: string
) => {
  if (!values && defaultValueName) {
    return options.filter(
      (option) => option.value?.toLowerCase() === defaultValueName.toLowerCase()
    );
  }
  if (!values) {
    return [];
  }
  return options.filter(
    (option) => values === option.value || values.includes(option.value)
  );
};

export const getInitialUsersPickerValue = (
  options: UserAvatars[],
  values: string[]
) => {
  return options && values
    ? options.filter((user: UserAvatars) => values.includes(user.value))
    : [];
};

type DatePickerValue = (
  rangeFilter: 'from' | 'to',
  filter?: RangeFilter
) => Date | null;
export const getInitialDatePickerValue: DatePickerValue = (
  rangeFilter,
  filter
) => {
  const value = get(filter, rangeFilter, '');
  return value ? new Date(value) : null;
};

export const parseURL = (text: string, linkClassName: string) => {
  const urlRegex =
    /((https?:\/\/)|(www\.))[a-z0-9]+([-.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?/gm;
  return text.replace(urlRegex, (url) => {
    if (!url.match('^https?://')) {
      url = `http://${url}`;
    }
    return `<a href='${url}' class='${linkClassName}' target='_blank' rel='noreferrer'>${url}</a>`;
  });
};

export const parseMention = (value: string, classes?: string) => {
  return value.replace(
    new RegExp('@\\[([^\\]]+?)\\]\\(([^\\)]+?)\\)', 'g'),
    (match, p1, offset) => {
      return `<span class='${classes || 'text-neutral'}'>@${p1}</span>`;
    }
  );
};

export const extractMentionedId = (value: string, classes?: string) => {
  const result = value.match(/@\[([^\]]+?)\]\(([^)]+?)\)/);
  if (result) {
    return result[2];
  }
};

export const extractContent = (html: string) => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
};

export const getInitials = ({
  firstName,
  lastName,
}: {
  firstName: string;
  lastName: string;
}) => `${firstName.charAt(0)}${lastName.charAt(0)}`;

export const getUserInitials = (user: AllUsersType) => {
  const nameIntials = `${get(user, 'data.firstName', '').charAt(0)}${get(
    user,
    'data.lastName',
    ''
  ).charAt(0)}`;
  if (!nameIntials) {
    return get(user, 'data.email', '').charAt(0);
  }
  return nameIntials;
};

export const getUserDisplayName = (user: AllUsersType) => {
  if (!get(user, 'data.firstName') && !get(user, 'data.lastName')) {
    return get(user, 'data.email', '');
  }

  return `${user.data.firstName} ${user.data.lastName}`;
};

export const scrollToBottomSmoothly = (element: HTMLElement) => {
  const scrollHeight = element.scrollHeight;
  const from = element.scrollTop;
  const duration = 500;

  const startTime = performance.now();
  function scrollStep(currentTime: number) {
    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    element.scrollTop = from + progress * (scrollHeight - from);
    if (elapsed < duration) {
      requestAnimationFrame(scrollStep);
    }
  }
  requestAnimationFrame(scrollStep);
};

export const formatter = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 2,
  minimumFractionDigits: 0,
});

export const difference = <T>(a: T[], b: T[]): T[] => {
  const onlyInA: T[] = a.filter((element) => !b.includes(element));
  const onlyInB: T[] = b.filter((element) => !a.includes(element));

  return onlyInA.concat(onlyInB);
};

export const getDeletedUser = (): UserAvatars[] => [
  {
    value: 'deleted-user',
    label: intl.get('DELETED_USER'),
    avatar: {
      initial: 'DU',
      imageSrc: userAvatar,
      name: intl.get('DELETED_USER'),
    },
  },
];
